import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { sparkToast } from '@spark';
import { data_from_server_post } from '@/shared/http/client';
import { DEFAULT_CLIENT_ID } from '@/config/client';
import WizardStepHeading from './WizardStepHeading';
import ToolDiscoveryChoiceCards from '../agentConfig/ToolDiscoveryChoiceCards';
import ManualToolSourceCard from '../agentConfig/ManualToolSourceCard';
import McpToolSourceCard from '../agentConfig/McpToolSourceCard';
import SwaggerToolSourceCard from '../agentConfig/SwaggerToolSourceCard';
import { useToolDiscovery } from '../agentConfig/useToolDiscovery';
import {
    TOOL_SOURCE_TYPES,
    agentConfigPayload,
    createToolSource,
    isBlankToolSource,
    toolSourceErrors,
    toolSourcesFromAgentConfig,
    toolSourcesHaveSelectedTools,
    toolSourcesHaveTools,
} from '../agentConfig/agentConfigModel';
import { executionUrlField, validateExecutionUrls } from '../agentConfig/executionValidation';
import '../agentConfig/agentConfig.css';

export const NO_TOOLS_TOAST = {
    title: 'No tools found',
    content: 'Add at least one tool source with at least one tool found to continue',
};

export const NO_SELECTED_TOOLS_TOAST = {
    title: 'No tools selected',
    content: 'Select at least one tool under "Tools Selected" to continue',
};

export const INVALID_SOURCES_TOAST = {
    title: 'Missing required fields',
    content: 'Please complete the highlighted fields to continue',
};

/**
 * Execution URL unreachable / erroring, or an MCP server missing a selected
 * tool or exposing it with another signature — `content` is the server's reason.
 */
export const INVALID_EXECUTION_TOAST = { title: 'Tool execution URL is not valid' };

const VALIDATION_TOAST_DURATION_MS = 8000;

/**
 * Wizard step "Agent Config" (agentic tasks only, Figma 3926-8787 and the
 * 4012-* variants): one or more Tool Discovery cards (Manual / MCP /
 * Swagger). Next persists the cards as `agent_config` on the usecase via
 * `update_llm_usecase` and is blocked (toast) until at least one source has
 * at least one tool found, at least one tool is selected across the sources,
 * and every non-blank source is complete. It then shows "Validating…" while
 * the backend probes each execution URL without running a tool
 * (`agent-tools/validate-execution`): an MCP URL must list every selected
 * tool with the same signature, any other URL only has to be reachable. A
 * failure blocks with a toast and highlights the field; nothing is shown
 * otherwise.
 */
const AgentConfigStep = ({ usecase, taskName, onBack, onNext, onSaved }) => {
    const [sources, setSources] = useState(() => toolSourcesFromAgentConfig(usecase?.agent_config));
    const [errorsById, setErrorsById] = useState({});
    const [isValidating, setIsValidating] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const discovery = useToolDiscovery();

    // Validation awaits the network; a result must not drive a save after the
    // step unmounted (Back) or the cards changed underneath it.
    const mountedRef = useRef(true);
    const sourcesRef = useRef(sources);
    sourcesRef.current = sources;
    useEffect(
        () => () => {
            mountedRef.current = false;
        },
        []
    );

    // Hydrate once from the stored config when it arrives after mount (deep
    // link resume) — never overwrite in-progress edits.
    const hydratedRef = useRef(Boolean(usecase?.agent_config));
    useEffect(() => {
        if (hydratedRef.current || !usecase?.agent_config) return;
        hydratedRef.current = true;
        setSources((prev) => (prev.length ? prev : toolSourcesFromAgentConfig(usecase.agent_config)));
    }, [usecase]);

    const addSource = useCallback((type) => {
        setSources((prev) => [...prev, createToolSource(type)]);
    }, []);

    const updateSource = useCallback((id, patch) => {
        setSources((prev) => prev.map((s) => (s.id === id ? { ...s, ...patch } : s)));
        // Any edit clears the highlighted errors of that card.
        setErrorsById((prev) => {
            if (!prev[id]) return prev;
            const { [id]: _removed, ...rest } = prev;
            return rest;
        });
    }, []);

    const removeSource = useCallback(
        (id) => {
            setSources((prev) => prev.filter((s) => s.id !== id));
            discovery.clearStatus(id);
            setErrorsById((prev) => {
                if (!prev[id]) return prev;
                const { [id]: _removed, ...rest } = prev;
                return rest;
            });
        },
        [discovery]
    );

    const hasTools = useMemo(() => toolSourcesHaveTools(sources), [sources]);
    const hasSelectedTools = useMemo(() => toolSourcesHaveSelectedTools(sources), [sources]);

    const handleNext = async () => {
        const nextErrors = {};
        sources.forEach((source) => {
            const errs = toolSourceErrors(source);
            if (Object.keys(errs).length) nextErrors[source.id] = errs;
        });
        setErrorsById(nextErrors);
        if (!hasTools) {
            sparkToast.error(NO_TOOLS_TOAST.title, {
                content: NO_TOOLS_TOAST.content,
                detailed: true,
                duration: 5000,
            });
            return;
        }
        // Tools were found but every one of them was unchecked (e.g. "Clear All").
        if (!hasSelectedTools) {
            sparkToast.error(NO_SELECTED_TOOLS_TOAST.title, {
                content: NO_SELECTED_TOOLS_TOAST.content,
                detailed: true,
                duration: 5000,
            });
            return;
        }
        if (Object.keys(nextErrors).length) {
            sparkToast.error(INVALID_SOURCES_TOAST.title, {
                content: INVALID_SOURCES_TOAST.content,
                detailed: true,
                duration: 5000,
            });
            return;
        }

        setIsValidating(true);
        const validation = await validateExecutionUrls(sources);
        if (!mountedRef.current) return;
        setIsValidating(false);
        // Cards edited while validating: the result no longer describes them.
        if (sourcesRef.current !== sources) return;
        if (!validation.ok) {
            setErrorsById((prev) => {
                const next = { ...prev };
                validation.failures.forEach(({ id, error }) => {
                    const source = sources.find((s) => s.id === id);
                    if (!source) return;
                    next[id] = { ...(next[id] || {}), [executionUrlField(source)]: error };
                });
                return next;
            });
            sparkToast.error(INVALID_EXECUTION_TOAST.title, {
                content: validation.failures.map((failure) => failure.error).join(' '),
                detailed: true,
                duration: VALIDATION_TOAST_DURATION_MS,
            });
            return;
        }

        if (!usecase?._id) {
            onNext();
            return;
        }
        setIsSaving(true);
        // Blank cards are dropped from what is saved; keep the UI list as-is
        // so nothing jumps while the request runs.
        const payload = agentConfigPayload(sources.filter((s) => !isBlankToolSource(s)));
        data_from_server_post(
            'update_llm_usecase',
            {
                usecase_id: usecase._id,
                client_id: usecase.client_id || DEFAULT_CLIENT_ID,
                usecase_name: usecase.usecase_name,
                sub_group_name: usecase.sub_group_name,
                usecase_goal: usecase.usecase_goal || '',
                task_type: usecase.task_type,
                group_name: usecase.group_name,
                is_multilabel: Boolean(usecase.is_multilabel),
                classify_speakers: Boolean(usecase.classify_speakers),
                confidence_rejection_enabled: usecase.confidence_rejection_enabled ?? true,
                rejection_label_name: usecase.rejection_label_name ?? null,
                task_languages: usecase.task_languages?.length ? usecase.task_languages : ['en'],
                agent_config: payload,
            },
            (result) => {
                if (!result?.success) {
                    setIsSaving(false);
                    sparkToast.error(result?.message || 'Could not save the agent configuration');
                    return;
                }
                const finish = () => {
                    setIsSaving(false);
                    onNext();
                };
                if (typeof onSaved === 'function') onSaved(finish);
                else finish();
            },
            (error) => {
                setIsSaving(false);
                sparkToast.error(
                    typeof error === 'string' && error ? error : 'Could not save the agent configuration'
                );
            }
        );
    };

    const renderSource = (source) => {
        const props = {
            source,
            errors: errorsById[source.id] || {},
            onChange: (patch) => updateSource(source.id, patch),
            onRemove: () => removeSource(source.id),
        };
        if (source.type === TOOL_SOURCE_TYPES.manual) {
            return <ManualToolSourceCard key={source.id} {...props} />;
        }
        if (source.type === TOOL_SOURCE_TYPES.mcp) {
            return <McpToolSourceCard key={source.id} {...props} discovery={discovery} />;
        }
        return <SwaggerToolSourceCard key={source.id} {...props} discovery={discovery} />;
    };

    return (
        <div className="cw-step cw-step--agent-config">
            <WizardStepHeading
                title="Agent Config"
                taskName={taskName}
                onBack={onBack}
                onNext={handleNext}
                nextLoading={isValidating || isSaving}
                nextLoadingLabel={isValidating ? 'Validating…' : 'Saving…'}
                nextDisableWhenReadOnly
            />
            <div className="cw-ac-intro">
                <h4 className="cw-ac-intro__title">Tool Discovery</h4>
                <p className="cw-ac-intro__subtitle">Add one or more tools</p>
            </div>
            {sources.map(renderSource)}
            <ToolDiscoveryChoiceCards
                heading={sources.length ? 'Add Another One' : null}
                onSelect={addSource}
                disabled={isValidating || isSaving}
            />
        </div>
    );
};

export default AgentConfigStep;
